"use client";

/**
 * FooterLetterform
 * ----------------
 * A giant brand-watermark that fills the footer width: the slogan
 * "AI that earns its Keep" as an oversized serif-italic letterform, with a
 * rotating tagline cycling beneath it. (The old "askFinz" brand state was
 * removed — the giant wordmark lives in FooterWatermark now.)
 *
 * The slogan renders STATICALLY (no state-toggle loop), so it's always legible;
 * motion comes from the tagline rotation. IntersectionObserver + visibilitychange
 * gate the rotation so nothing ticks while the footer is parked or the tab is hidden.
 */

import { useEffect, useRef, useState } from "react";

const VIEW_W = 1297;
const VIEW_H = 264;

// "AI that earns its Keep" — words joined by fixed spacers so word spacing reads
// cleanly at any width without depending on UA whitespace.
const SLOGAN_SEGMENTS = [
  ["A", "I"],
  ["t", "h", "a", "t"],
  ["e", "a", "r", "n", "s"],
  ["i", "t", "s"],
  ["K", "e", "e", "p"],
];
const SLOGAN_FLAT: string[] = SLOGAN_SEGMENTS.flatMap((seg, i, all) =>
  i < all.length - 1 ? [...seg, " "] : seg,
);

// Rotating subtitle under the slogan — cycles through all four, one after the
// other. All live in the DOM (sr-only list) for a11y + crawlers.
const TAGLINES = [
  "Why dip your toes when you can dive right in.",
  "Skip the shallow end. Dive straight in.",
  "Why start with the basics when you can go deep.",
  "Skip the tutorial. Build something real.",
] as const;

export default function FooterLetterform() {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [tagIdx, setTagIdx] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setInView(entry.isIntersecting);
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let id: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (id !== null || document.visibilityState !== "visible") return;
      id = setInterval(() => setTagIdx((i) => (i + 1) % TAGLINES.length), 4500);
    };
    const stop = () => {
      if (id !== null) {
        clearInterval(id);
        id = null;
      }
    };
    const onVis = () => (document.visibilityState === "visible" ? start() : stop());
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [inView]);

  return (
    <div
      ref={wrapRef}
      role="img"
      aria-label="AI that earns its Keep."
      className="footer-letterform-region text-foreground/15 dark:text-foreground/20"
      data-running={inView ? "1" : "0"}
    >
      <svg
        aria-hidden="true"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="footer-letterform-svg block w-full h-auto select-none"
      >
        {/* Static slogan — no state class, so it stays fully visible.

            textLength IS LOad-BEARING, not decoration. At fontSize 160 this 22-glyph slogan
            measures roughly 1,750px in an italic serif, inside a viewBox 1,297 wide — so it was
            centred and spilling past BOTH edges of the box. On a wide screen `max-height` hid most
            of it; on a phone the SVG scales to the viewport and the overflow became the visible
            bug. Worse, the amount of spill depended on which font had loaded, because Georgia and
            Source Serif 4 do not share metrics.

            Pinning the advance width to the box makes the fit exact at every viewport and under
            every fallback font. `spacingAndGlyphs` distributes the correction across the letter-
            forms as well as the gaps, which keeps an italic serif looking condensed rather than
            gap-toothed. */}
        <text
          x={VIEW_W / 2}
          y={VIEW_H / 2 + 4}
          textLength={VIEW_W - 48}
          lengthAdjust="spacingAndGlyphs"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily='var(--font-serif), "Source Serif 4", Georgia, serif'
          fontStyle="italic"
          fontWeight={400}
          fontSize={160}
          letterSpacing="-0.02em"
          fill="currentColor"
        >
          {SLOGAN_FLAT.map((ch, i) =>
            ch === " " ? (
              <tspan key={`sp-${i}`} dx="0.18em">
                {" "}
              </tspan>
            ) : (
              <tspan key={`s-${i}`}>{ch}</tspan>
            ),
          )}
        </text>
      </svg>

      <div className="footer-letterform-tagline-slot mt-3 md:mt-4 text-center min-h-[2.6em] md:min-h-[1.6em] relative">
        <p
          key={tagIdx}
          aria-hidden="true"
          className="footer-letterform-tagline footer-letterform-tagline-anim font-serif italic text-foreground/65 text-[clamp(1rem,2vw,1.4rem)] tracking-tight px-4"
        >
          {TAGLINES[tagIdx]}
        </p>
        <ul className="sr-only">
          {TAGLINES.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
